// @ts-ignore
import { Ref } from 'vue'
// @ts-ignore
import jwtDecode from 'jwt-decode'
// @ts-ignore
import jwt from 'jwt-simple'
// @ts-ignore
import { FetchContext } from 'ofetch'
// import { useSID } from "./sio";

import dayjs from 'dayjs'
// @ts-ignore
import utc from 'dayjs/plugin/utc'
// @ts-ignore
import timezone from 'dayjs/plugin/timezone'

interface TokenHeader {
	alg: jwt.TAlgorithm
	typ?: string
}

interface TokenData {
	user_id: number
	exp: number
	iat: number
	jti: string
	scope?: string
	scheme: string
}

interface OAuth2Definition {
	name: string
	authURL: string
	profileURL: string
	refreshURL?: string
}


interface AuthCredentials {
	login: string,
	password: string
}

interface AuthOptions {
	onError?: Function,
	onSuccess?: Function
}

interface AuthResponseData {
	scheme: string
	access_token: string
	refresh_token: string
	user?: unknown
	detail?: string
}

interface AuthRefreshData {
	scheme: string
	access_token: string
	refresh_token: string
}

interface AuthResponse {
	ok: boolean
	status: number
	_data: AuthResponseData,
}

const TokenSerializer = {
	read: (v: string) => {
		if (!v || !v.length)
			return null
		return new Token(v)
	},
	write: (v: Token) => {
		return v ? v.toString() : null
	}
}

export class Token {
	public token: string
	public data: TokenData
	public header: TokenHeader
	public constructor(token: string, public_key: string|null = null) {
		this.token = token
		this.header = jwtDecode(this.token, {header: true})
		if (!public_key)
			this.data = jwtDecode(this.token)
		else {
			try {
				this.data = jwt.decode(this.token, public_key, false, this.header.alg)
			} catch (e: any) {
				console.log(e)
				// temporary fix
				this.data = jwtDecode(this.token)
			}
		}
	}
	public toString():string {
		return this.token.toString()
	}
	public isExpired(x: number = 0): boolean {
		return this.expiredIn - x <= 0
	}
	public get expiredIn(): number {
		dayjs.extend(utc)
		dayjs.extend(timezone)
		let utc_timestamp= dayjs.utc().unix()
		return this.data.exp - utc_timestamp || 0
	}
}

class OAuth2 {
	private name: string
	apiEndpoint: string
	private authURL: string
	private profileURL: string
	private refreshURL?: string
	scheme: Ref = null
	accessToken = null
	refreshToken = null
	isRefreshing = ref<boolean>(false)
	profile = ref()

	constructor(opts: any) {
		let { name, apiEndpoint, authURL, profileURL, refreshURL, scheme = 'Bearer' } = opts
		if (!apiEndpoint) {
			const rConf = useRuntimeConfig();
			apiEndpoint = rConf.public.apiBase;
		}
		this.name = name;
		this.scheme = useSessionStorage(`${this.name}:token_scheme`, scheme)
		this.apiEndpoint = apiEndpoint;
		this.authURL = authURL;
		this.refreshURL = refreshURL;
		this.profileURL = profileURL;
		this.accessToken = useSessionStorage(`${this.name}:access_token`,  null, {
			serializer: TokenSerializer,
			onError: (error) => {
    			console.error(`Storage operation ${this.name}:access_token failed:`, error)
  			}
		})
		this.refreshToken = useSessionStorage(`${this.name}:refresh_token`, null,  {
			serializer: TokenSerializer,
			onError: (error) => {
    			console.error(`Storage operation ${this.name}:refresh_token failed:`, error)
  			}
		})
		this.slidingToken = computed(() => {
			if (this.accessToken.value) {
				return this.refreshToken.value ? this.refreshToken.value : this.accessToken.value
			}
			return null
		})
	}

	expired = (x: number = 0): boolean => {
		return this.slidingToken.value ? this.slidingToken.value.isExpired(x) : true
	}

	authenticate = async (credentials: AuthCredentials, options: AuthOptions = {}) => {
		const { onError, onSuccess, scope, ...opts } = options

		const body = new FormData()
		body.append('username', credentials.login)
		body.append('password', credentials.password)
		if(scope) {
			body.append('scope', scope)
		}

		const currentOptions = {
			...opts,
			baseURL: this.apiEndpoint,
			method: 'POST',
			body,
			// @ts-ignore
			onResponse: async ({ response }): Promise<any> => {
				if (!response.ok && onError)
					onError({msg: response._data.detail, status: response.status})
				else if (response.ok) {
					if (response._data.scheme)
						this.scheme.value = response._data.scheme;
					this.accessToken.value = response._data.access_token ? new Token(response._data.access_token) : null
					this.refreshToken.value = response._data.refresh_token ? new Token(response._data.refresh_token) : null
					this.profile.value = response._data.user
					if (onSuccess) {
						onSuccess({
							accessToken: this.accessToken,
							profile: this.profile
						})
					}
				}
			}
		};
		return await $fetch(this.authURL, currentOptions)
	}

	waitRefresh = (): Promise<boolean> => {
		return new Promise((resolve) => {
			const checker = () => {
				const timeout = setTimeout(checker, 10)
				if (!this.isRefreshing.value) {
					clearTimeout(timeout)
					return resolve(true)
				}
			}
			checker()
		})
	}

	refresh = async (): Promise<void> => {
		if (!this.isRefreshing.value) {
			this.isRefreshing.value = true
			try {
				const data: AuthRefreshData = await $fetch(this.refreshURL, {
					baseURL: this.apiEndpoint,
					method: 'POST',
					body: {token: this.slidingToken.value.toString()},
				})
				if (data.scheme)
					this.scheme.value = data.scheme
				this.accessToken.value = data.access_token ? new Token(data.access_token) : null
				this.refreshToken.value = data.refresh_token ? new Token(data.refresh_token) : null
			} catch (e) {
				this.logout()
			} finally {
				this.isRefreshing.value = false;
			}

		} else {
			await this.waitRefresh()
		}
	}

	hasAccess = (check: string|string[]): boolean => {
		if (this.expired())
			return false
		if (this.profile.value.is_staff || this.profile.value.is_superuser && this.profile.value.is_active)
			return true
		if (!Array.isArray(check))
			check = [check]
		return check.filter((p: string) => this.profile.value.all_permissions.indexOf(p) !== -1).length > 0
	}

	hasPerm = (perm: string): boolean => {
		if (this.expired())
			return false
		if (this.profile.value.is_superuser)
			return true
		return this.profile.value.all_permissions.indexOf(perm) !== -1
	}

	api = (endpoint: string, options: any = {}) => {
		let { onRequest, ...opts } = options

		opts.baseURL = this.apiEndpoint
		opts.onRequest = async (ctx: FetchContext) => {
			if (this.accessToken.value) {
				if (this.expired())
					return this.logout();
				if(this.accessToken.value.isExpired())
					await this.refresh();
				ctx.options.headers = {
					...opts.headers,
					Authorization: `${this.scheme.value} ${this.accessToken.value.toString()}`,
					'Accept-Language': 'de' //$i18n.locale.value
				};

				if (onRequest)
					return await onRequest(ctx);
				else
					return ctx;
			} else
				return ctx;
		}
		// @ts-ignore
		return $fetch(endpoint, opts);
	}

	fetchProfile = async (): Promise<void> => {
		this.profile.value = await this.api(this.profileURL)
	}

	public logout = () => {
		this.accessToken.value = null
		this.refreshToken.value = null

		location.reload()
	}
}

let sessions = shallowRef<any>({})

export const useAuthSession = (name?: string) => {
	if (!name)
		name = 'local-intranet'
	if (name === 'local-intranet' && Object.keys(sessions.value).indexOf('local-intranet') === -1) {
		sessions.value['local-intranet'] = new OAuth2({
			name: 'local-intranet',
			authURL: '/rest/auth/login',
			refreshURL: '/rest/auth/refresh',
			profileURL: '/rest/auth/profile'
		})
	}
	return sessions.value[name]
}
